import React, { useState } from 'react';
import { Box, Typography, Divider, CircularProgress, TextField, InputAdornment, Collapse } from '@material-ui/core';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { selectTollData, selectTollLoading, selectTollCountryOverrides, setTollCountryOverride } from '../../store/slices/tollsSlice';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';

// Country flag emoji from 2-letter code
function countryFlag(code) {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().slice(0, 2);
  return String.fromCodePoint(
    ...upper.split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

// Country name mapping
const COUNTRY_NAMES = {
  PRT: 'Portugal', PT: 'Portugal',
  ESP: 'Espanha', ES: 'Espanha',
  FRA: 'França', FR: 'França',
  DEU: 'Alemanha', DE: 'Alemanha',
  ITA: 'Itália', IT: 'Itália',
  BEL: 'Bélgica', BE: 'Bélgica',
  NLD: 'Países Baixos', NL: 'Países Baixos',
  AUT: 'Áustria', AT: 'Áustria',
  CHE: 'Suíça', CH: 'Suíça',
  CZE: 'Chéquia', CZ: 'Chéquia',
  POL: 'Polónia', PL: 'Polónia',
  HUN: 'Hungria', HU: 'Hungria',
  LUX: 'Luxemburgo', LU: 'Luxemburgo',
  GBR: 'Reino Unido', GB: 'Reino Unido',
};

function TollsPanel({ lang, currency: routeCurrency, tripMultiplier = 1, roadBreakdown }) {
  const dispatch = useDispatch();
  const tollData = useSelector(selectTollData);
  const loading = useSelector(selectTollLoading);
  const overrides = useSelector(selectTollCountryOverrides);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Card colapsável — mostra sempre o total (essencial) e esconde o
  // breakdown por país e travessias por defeito. Mesmo padrão de
  // FuelCountryPanel / "Trajeto completo".
  const [expanded, setExpanded] = useState(false);

  function handleStartEdit(cc, currentCost) {
    setEditingCountry(cc);
    setEditValue(String(Number(currentCost).toFixed(2)));
  }
  function handleSaveEdit(cc) {
    const val = parseFloat(editValue.replace(',', '.'));
    if (!isNaN(val) && val >= 0) {
      dispatch(setTollCountryOverride({ countryCode: cc, cost: val }));
    } else {
      dispatch(setTollCountryOverride({ countryCode: cc, cost: null }));
    }
    setEditingCountry(null);
  }

  if (loading) {
    return (
      <Box>
        <SectionTitle icon={MonetizationOnIcon}>{t(lang, 'tollCosts')}</SectionTitle>
        <GlassCard flat padding="sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CircularProgress size={14} />
          <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            {t(lang, 'tollsLoading')}
          </Typography>
        </GlassCard>
      </Box>
    );
  }

  if (!tollData) return null;

  const { totalCost, currency, tolls, countryCosts, cached, cacheSource, crossings } = tollData;
  const cur = currency || 'EUR';

  // Group tolls by country
  const byCountry = {};
  (tolls || []).forEach(toll => {
    const cc = toll.countryCode || 'OTHER';
    if (!byCountry[cc]) byCountry[cc] = { tolls: [], total: 0 };
    byCountry[cc].tolls.push(toll);
    byCountry[cc].total += toll.cost;
  });

  // Aplicar overrides — substitui o total da HERE pelo valor inserido.
  // Recalcula o total global tendo em conta os overrides.
  const effectiveByCountry = Object.fromEntries(
    Object.entries(byCountry).map(([cc, data]) => {
      const override = overrides[cc];
      return [cc, { ...data, effectiveTotal: override != null ? override : data.total, isOverride: override != null }];
    })
  );
  const recomputedTotal = Object.values(effectiveByCountry).reduce((s, d) => s + d.effectiveTotal, 0);
  const effectiveTotalCost = recomputedTotal * tripMultiplier;
  const isTollFree = recomputedTotal === 0;
  const motorwayKm = roadBreakdown?.motorway || 0;
  const totalClassifiedKm = roadBreakdown
    ? (roadBreakdown.motorway || 0) + (roadBreakdown.highway || 0) + (roadBreakdown.national || 0) + (roadBreakdown.local || 0)
    : 0;
  // Note kept for reference, currently never shown — the "free motorways" note was
  // misleading for routes outside the EU (e.g. Angola) where tolls are simply not
  // covered by the API. Re-enable with a proper country check if needed later.
  const showFreeMotorwayNote = false;

  const countryCount = Object.keys(effectiveByCountry).length;
  const crossingCount = Array.isArray(crossings) ? crossings.length : 0;

  return (
    <Box>
      {/* Header clicável — colapsa/expande os detalhes. Mantém o badge
          "cached" sempre visível mesmo no estado colapsado para o
          operador saber a fonte. */}
      <Box
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(v => !v)}
      >
        <SectionTitle icon={MonetizationOnIcon}>{t(lang, 'tollCosts')}</SectionTitle>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!isTollFree && (
            <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
              {countryCount} {countryCount === 1 ? (lang === 'pt' ? 'país' : 'country') : (lang === 'pt' ? 'países' : 'countries')}
              {crossingCount > 0 ? ` · ${crossingCount} ⛴️` : ''}
            </Typography>
          )}
          {cached && (
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: cacheSource === 'redis' ? 'rgba(34,197,94,0.1)' : 'rgba(59,130,246,0.08)',
              border: `1px solid ${cacheSource === 'redis' ? 'rgba(34,197,94,0.35)' : 'rgba(59,130,246,0.35)'}`,
              borderRadius: 20, padding: '3px 9px', whiteSpace: 'nowrap',
            }}>
              <Box style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: cacheSource === 'redis' ? '#22c55e' : '#3b82f6',
              }} />
              <Typography style={{
                fontSize: '0.54rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                color: cacheSource === 'redis' ? '#16a34a' : '#3b82f6', lineHeight: 1,
              }}>
                {t(lang, 'tollsCached')}
              </Typography>
            </Box>
          )}
          {expanded ? <ExpandLessIcon style={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
        </Box>
      </Box>

      <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Total toll cost */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t(lang, 'totalTollCost')}
          </Typography>
          <Typography style={{
            fontSize: '1.3rem', fontWeight: 700,
            color: isTollFree ? '#22c55e' : '#8b5cf6',
          }}>
            {isTollFree ? t(lang, 'tollFree') : `${effectiveTotalCost.toFixed(2)} ${cur}`}
          </Typography>
        </Box>

        {showFreeMotorwayNote && (
          <Box style={{
            display: 'flex', alignItems: 'flex-start', gap: 6,
            background: 'rgba(34,197,94,0.08)',
            border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 6, padding: '6px 8px',
          }}>
            <InfoOutlinedIcon style={{ fontSize: 14, color: '#16a34a', marginTop: 1, flexShrink: 0 }} />
            <Typography style={{ fontSize: '0.65rem', color: '#166534', lineHeight: 1.35 }}>
              {t(lang, 'tollFreeMotorwayNote')}
            </Typography>
          </Box>
        )}

        {!isTollFree && (
          <Collapse in={expanded}>
            <Divider style={{ margin: '2px 0' }} />

            {/* Travessias marítimas + Eurotúnel — detectadas via
                section.transport.mode === 'ferry'. HERE não devolve
                tarifas destas, por isso a estimativa vem de uma tabela
                no backend (CROSSING_FARES) que pode ser ajustada por
                veículo (car / light_truck / truck_12m / truck_18m). */}
            {Array.isArray(crossings) && crossings.length > 0 && (
              <Box style={{ marginBottom: 6, background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 6, padding: '6px 8px' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: '0.9rem' }}>⛴️</span>
                  <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {lang === 'pt' ? 'Travessias / Eurotúnel' : 'Crossings / Eurotunnel'}
                  </Typography>
                  <Box style={{ flex: 1 }} />
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1' }}>
                    {(crossings.reduce((s, c) => s + c.cost, 0) * tripMultiplier).toFixed(2)} {cur}
                  </Typography>
                </Box>
                {crossings.map((c, i) => (
                  <Box key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0 2px 22px' }}>
                    <Typography style={{ fontSize: '0.66rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </Typography>
                    <Typography style={{ fontSize: '0.66rem', color: '#0f172a', fontWeight: 500, marginLeft: 8 }}>
                      {(c.cost * tripMultiplier).toFixed(2)} {cur}
                    </Typography>
                  </Box>
                ))}
                <Typography style={{ fontSize: '0.54rem', color: '#94a3b8', fontStyle: 'italic', marginTop: 3 }}>
                  {lang === 'pt' ? 'Estimativa por categoria de veículo (HERE não inclui ferries)' : 'Estimate by vehicle class (HERE excludes ferries)'}
                </Typography>
              </Box>
            )}

            {/* Breakdown by country — click no valor edita override */}
            {Object.entries(effectiveByCountry).map(([cc, data]) => {
              const flag = countryFlag(cc.length === 3 ? cc.slice(0, 2) : cc);
              const countryName = COUNTRY_NAMES[cc] || cc;
              const isEditing = editingCountry === cc;
              return (
                <Box key={cc}>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.9rem' }}>{flag}</span>
                      <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 600 }}>
                        {countryName}
                      </Typography>
                      {data.isOverride && (
                        <Typography style={{ fontSize: '0.55rem', color: '#3b82f6', fontWeight: 700, background: '#dbeafe', padding: '1px 6px', borderRadius: 4, marginLeft: 2 }}>
                          editado
                        </Typography>
                      )}
                    </Box>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onBlur={() => handleSaveEdit(cc)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cc); if (e.key === 'Escape') { setEditingCountry(null); } }}
                        autoFocus
                        InputProps={{
                          endAdornment: <InputAdornment position="end" style={{ fontSize: '0.62rem' }}>{cur}</InputAdornment>,
                          style: { fontSize: '0.74rem', height: 24, width: 110 },
                        }}
                        style={{ margin: 0 }}
                      />
                    ) : (
                      <Box
                        onClick={() => handleStartEdit(cc, data.effectiveTotal)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                          padding: '1px 6px', borderRadius: 4,
                          background: data.isOverride ? 'rgba(59,130,246,0.08)' : 'transparent',
                          border: `1px solid ${data.isOverride ? 'rgba(59,130,246,0.3)' : 'transparent'}`,
                          transition: 'all 0.15s',
                        }}
                        title={lang === 'pt' ? 'Clica para editar (override)' : 'Click to edit (override)'}
                      >
                        <Typography style={{
                          fontSize: '0.78rem', fontWeight: 600,
                          color: data.isOverride ? '#3b82f6' : '#8b5cf6',
                        }}>
                          {(data.effectiveTotal * tripMultiplier).toFixed(2)} {cur}
                        </Typography>
                        <EditIcon style={{ fontSize: 10, color: '#94a3b8', opacity: 0.6 }} />
                      </Box>
                    )}
                  </Box>

                  {/* Individual tolls */}
                  {data.tolls.map((toll, idx) => (
                    <Box key={idx} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '3px 0 3px 22px',
                      borderBottom: idx < data.tolls.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                    }}>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{
                          fontSize: '0.68rem', color: '#64748b',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {toll.name || t(lang, 'tollBooth')}
                        </Typography>
                        {toll.tollSystem && (
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                            {toll.tollSystem}
                          </Typography>
                        )}
                      </Box>
                      <Typography style={{ fontSize: '0.68rem', color: '#0f172a', fontWeight: 500, flexShrink: 0, marginLeft: 8 }}>
                        {toll.cost.toFixed(2)} {toll.currency || cur}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}

            {/* Payment methods hint */}
            {tolls?.[0]?.paymentMethods?.length > 0 && (
              <>
                <Divider style={{ margin: '2px 0' }} />
                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                    {t(lang, 'tollPaymentMethods')}
                  </Typography>
                  <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                    {[...new Set(tolls.flatMap(t => t.paymentMethods || []))]
                      .map(m => m === 'transponder' ? 'Via Verde' : m === 'creditCard' ? 'Cartão' : m === 'cash' ? 'Dinheiro' : m)
                      .slice(0, 3)
                      .join(', ')}
                  </Typography>
                </Box>
              </>
            )}
          </Collapse>
        )}
      </GlassCard>
    </Box>
  );
}

export default TollsPanel;
